import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Button from "../../components/Button/Button";
import Checkmark from "../../components/Checkmark/Checkmark";
import "./ProductPage.scss";
import frcophth2QuestionsPage from "../../assets/products/frcophth2-questions-page.png";
import frcophth2ModulesPage from "../../assets/products/frcophth2-modules-page.png";
import frcophth2ModuleProgress from "../../assets/products/frcophth2-module-progress.png";
import frcophth2TimedExamPage from "../../assets/products/frcophth2-timed-exam-page.png";
import { FaCheckCircle, FaBook, FaStar, FaBrain, FaChartLine, FaBullseye } from "react-icons/fa";
import { topicIcons } from "./topicIcons";

interface FRCOphthPart2PageProps {
  product: {
    id: string;
    price: number;
    length: string;
    comingSoon: boolean;
  };
}

const FRCOphthPart2Page = ({ product }: FRCOphthPart2PageProps) => {
  const part2Topics = [
    "Cornea & External Disease",
    "Cataract & Refractive Surgery",
    "Glaucoma",
    "Retina",
    "Uveitis",
    "Neurology & Pupils",
    "Paediatric Ophthalmology & Strabismus",
    "Oculoplastics",
    "Ocular Oncology",
    "Trauma",
    "Neuro-imaging & Investigations"
  ];

  return (
    <>
      <NavBar />
      <div id="ProductPage" className="product-page--oq">
        <section className="oq-product-hero" aria-label="FRCOphth Part 2">
          <div className="site-container oq-product-hero__inner">
            <div className="oq-product-hero__copy">
              <p className="oq-product-hero__eyebrow">FRCOphth Part 2</p>
              <h1 className="oq-product-hero__title">FRCOphth Part 2 Question Bank</h1>
              <p className="oq-product-hero__lead">
                10,000+ clinically focused MCQs covering <strong>every module and every syllabus area</strong> for Part
                2 Written—<strong>exam-style stems</strong> that match real papers, plus <strong>timed mock exams</strong>.
                Built for exam day. <strong>Co-built by ophthalmologists, for ophthalmologists.</strong>
              </p>
              <div className="oq-product-hero__pricing">
                <span className="oq-product-hero__price">£{product.price}</span>
                <span className="oq-product-hero__length">/ {product.length}</span>
              </div>
              {!product.comingSoon && (
                <div className="oq-product-hero__signup">
                  <Button
                    text="Buy FRCOphth Part 2 Now"
                    linkTo={`/sign-up?product=${product.id}`}
                    type="navy"
                  />
                </div>
              )}
            </div>
            <div className="oq-product-hero__visual">
              <div className="oq-product-hero__frame">
                <img
                  className="oq-product-hero__image"
                  src={frcophth2ModulesPage}
                  alt="FRCOphth Part 2 modules and syllabus areas in OphthalmologyQ"
                  width={3146}
                  height={1784}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="product-sales-content">
          <div className="site-container">
            
            {/* Introduction Section */}
            <section className="sales-section intro-section">
              <p className="lead-text">
                FRCOphth Part 2 tests clinical reasoning and judgement—many candidates fall short because they do not
                practise with enough questions that match the <strong>wording, format, and difficulty of the real exam</strong>.
              </p>
              <p className="solution-text">
                <strong>OphthalmologyQ is co-built by ophthalmologists for ophthalmologists</strong>—with full syllabus
                depth across <strong>every module and area</strong>.
              </p>
            </section>

            {/* Questions Section */}
            <section className="sales-section questions-section">
              <div className="content-with-image">
                <div className="content-side">
                  <div className="section-header">
                    <FaBook className="section-icon" />
                    <h2>10,000+ Clinically Relevant MCQs</h2>
                  </div>
                  <p className="lead-text">
                    Access one of the largest Part 2 libraries available: 10,000+ questions across{" "}
                    <strong>every module and every area</strong> of the clinical syllabus, including:
                  </p>
                  <div className="topics-list">
                    <div className="topics-grid">
                      {part2Topics.map((topic, index) => (
                        <div key={index} className="topic-card">
                          <div className="topic-icon">{topicIcons[topic]}</div>
                          <span className="topic-name">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="tagline-text">
                    No topic left untested. No blind spots on exam day.
                  </p>
                </div>
                <div className="image-side">
                  <img src={frcophth2ModulesPage} alt="FRCOphth Part 2 Question Bank Modules" className="product-screenshot" />
                </div>
              </div>
            </section>

            {/* High-Yield Questions Section */}
            <section className="sales-section highlight-section">
              <div className="highlight-header">
                <div className="highlight-badge highlight-badge-coming-soon">
                  <FaStar className="star-icon" />
                  <span>Coming Soon</span>
                </div>
                <h2>100s of high-yield exam questions (coming soon)</h2>
              </div>
              <p className="lead-text">
                A dedicated collection of 100s high-yield MCQs focused on the topics that matter most for Part 2 success will be available soon:
              </p>
              <div className="highlight-features">
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Recurrent exam themes</h3>
                    <p>Questions covering the most frequently tested areas</p>
                  </div>
                </div>
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Common Part 2 pitfalls</h3>
                    <p>Patterns and concepts that appear year after year</p>
                  </div>
                </div>
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Management algorithms frequently tested</h3>
                    <p>The questions that separate successful candidates</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Explanations Section */}
            <section className="sales-section explanations-section">
              <div className="content-with-image reverse">
                <div className="image-side">
                  <img src={frcophth2QuestionsPage} alt="FRCOphth Part 2 Question with Explanation" className="product-screenshot" />
                </div>
                <div className="content-side">
                  <div className="section-header">
                    <FaBrain className="section-icon" />
                    <h2>Detailed Explanations That Teach Clinical Thinking</h2>
                  </div>
                  <p className="lead-text">
                    Every question includes:
                  </p>
                  <div className="simple-list">
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Clear justification for the correct answer</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Explanations for incorrect options</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Practical clinical pearls relevant to exams and real practice</p>
                    </div>
                  </div>
                  <p className="emphasis-text">
                    Learn how examiners think, not just what they ask.
                  </p>
                </div>
              </div>
            </section>

            {/* Analytics Section */}
            <section className="sales-section analytics-section">
              <div className="content-with-image">
                <div className="content-side">
                  <div className="section-header">
                    <FaChartLine className="section-icon" />
                    <h2>analytics for Strategic Revision</h2>
                  </div>
                  <p className="lead-text">
                    Use analytics to:
                  </p>
                  <div className="simple-list">
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Track performance by subspecialty</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Identify consistently weak areas</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Prioritise revision efficiently</p>
                    </div>
                  </div>
                  <p className="tagline-text">
                    Spend time where it will improve your score the most.
                  </p>
                </div>
                <div className="image-side">
                  <img src={frcophth2ModuleProgress} alt="FRCOphth Part 2 Module Progress Analytics" className="product-screenshot" />
                </div>
              </div>
            </section>

            {/* Exam Success Section */}
            <section className="sales-section exam-success-section">
              <div className="content-with-image reverse">
                <div className="image-side">
                  <img src={frcophth2TimedExamPage} alt="FRCOphth Part 2 Timed Exam Practice" className="product-screenshot" />
                </div>
                <div className="content-side">
                  <div className="section-header">
                    <FaBullseye className="section-icon" />
                    <h2>Designed for Real Exam Conditions</h2>
                  </div>
                  <p className="lead-text">
                    Everything is built around Part 2 exam reality—<strong>timed mock exams</strong>, stems that mirror{" "}
                    <strong>real question wording and format</strong>, and difficulty calibrated to what you will face.
                  </p>
                  <div className="simple-list oq-icon-list">
                    <div className="list-item">
                      <FaCheckCircle aria-hidden />
                      <p>Complex clinical stems in real exam style</p>
                    </div>
                    <div className="list-item">
                      <FaCheckCircle aria-hidden />
                      <p>Exam-accurate difficulty</p>
                    </div>
                    <div className="list-item">
                      <FaCheckCircle aria-hidden />
                      <p>Decision-based questions</p>
                    </div>
                    <div className="list-item">
                      <FaCheckCircle aria-hidden />
                      <p>Timed practice &amp; mocks under pressure</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Pricing Section */}
            <section className="sales-section pricing-section">
              <div className="pricing-card">
                <h3>Simple Pricing</h3>
                <h3 className="pricing-card__subtitle">FRCOphth Part 2 Question Bank</h3>
                <div className="pricing-amount">
                  <span className="price-large">£{product.price}</span>
                  <span className="price-period">— 6 months full access</span>
                </div>
                <div className="pricing-includes">
                  <h4>Includes:</h4>
                  <ul>
                    <li><Checkmark /> 10,000+ MCQs (every module &amp; area)</li>
                    <li><Checkmark /> 100s of high-yield exam questions (coming soon)</li>
                    <li><Checkmark /> Timed practice &amp; mock exams</li>
                    <li><Checkmark /> Full explanations &amp; analytics</li>
                    <li><Checkmark /> Mobile &amp; desktop access</li>
                  </ul>
                </div>
                <div className="pricing-cta">
                  <Button
                    text="Get Started Now"
                    linkTo={`/sign-up?product=${product.id}`}
                    type="navy"
                  />
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FRCOphthPart2Page;

